import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ProjectService, Project } from '../../services/project.service';
import { WebsiteService, SupportedLanguage, LanguageOption } from '../../services/website.service';
import { NotificationService } from '../../services/notification.service';
import { NotificationComponent } from '../../components/notification/notification.component';
import { ConfirmModalComponent } from '../../components/confirm-modal/confirm-modal.component';
import { Subject } from 'rxjs';
import { takeUntil} from 'rxjs/operators';

type SortColumn = 'title' | null;
type SortDirection = 'asc' | 'desc' | null;

@Component({
  selector: 'app-projects',
  standalone: true,
  imports: [CommonModule, FormsModule, NotificationComponent, ConfirmModalComponent],
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.scss']
})
export class ProjectsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  projects: Project[] = [];
  filteredProjects: Project[] = [];
  availableDomains: string[] = [];
  loading = false;
  
  // Sorting and filtering
  sortColumn: SortColumn = null;
  sortDirection: SortDirection = null;
  filterText = '';
  showAllDomains = false;
  allProjects: Project[] = [];
  
  // Current language
  currentLocale: SupportedLanguage = 'nl';
  availableLanguages: LanguageOption[] = [];
  
  // Delete modal
  showDeleteModal = false;
  projectToDelete: Project | null = null;
  
  // Domain editing
  editingDomains: { [projectId: string]: boolean } = {};

  constructor(
    private router: Router,
    private projectService: ProjectService,
    private websiteService: WebsiteService,
    private notificationService: NotificationService
  ) {}

  async ngOnInit() {
    this.loading = true;
    
    // Load available domains
    this.availableDomains = await this.websiteService.getWebsites();
    
    // Subscribe to current domain changes and load languages
    this.websiteService.currentDomain$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (domain) => {
        if (domain) {
          await this.loadLanguagesForDomain(domain);
        }
        this.loadProjects();
      });
    
    // Subscribe to language changes
    this.websiteService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        this.currentLocale = lang;
      });
    
    // Initial load of languages
    const currentDomain = this.websiteService.getCurrentDomain();
    if (currentDomain) {
      await this.loadLanguagesForDomain(currentDomain);
    }
    
    this.loadProjects();
  }

  async loadLanguagesForDomain(domain: string) {
    const settings = await this.websiteService.getWebsiteSettings(domain);
    if (settings?.availableLanguages && settings.availableLanguages.length > 0) {
      this.availableLanguages = settings.availableLanguages;
    } else {
      // Fallback to default languages
      this.availableLanguages = [
        { code: 'nl', name: 'Nederlands' },
        { code: 'en', name: 'English' }
      ];
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects() {
    this.loading = true;
    this.projectService.getProjects()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (projects) => {
          this.allProjects = projects;
          this.applyDomainFilter();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading projects:', error);
          this.notificationService.show({
            message: 'Fout bij laden van projecten',
            type: 'error'
          });
          this.loading = false;
        }
      });
  }

  applyDomainFilter() {
    if (this.showAllDomains) {
      this.projects = [...this.allProjects];
    } else {
      const currentDomain = this.websiteService.getCurrentDomain();
      if (currentDomain) {
        this.projects = this.allProjects.filter(p => {
          const domains = p.domains || [];
          return domains.includes(currentDomain);
        });
      } else {
        this.projects = [...this.allProjects];
      }
    }
    this.applyFiltersAndSort();
  }

  onToggleAllDomains() {
    this.showAllDomains = !this.showAllDomains;
    this.applyDomainFilter();
  }

  // Filtering and sorting
  applyFiltersAndSort() {
    let filtered = [...this.projects];
    
    // Apply text filter
    if (this.filterText.trim()) {
      const query = this.filterText.toLowerCase();
      filtered = filtered.filter(p => {
        const title = this.getProjectTitle(p).toLowerCase();
        const mainCategory = (p.mainCategory || '').toLowerCase();
        const categories = (p.categories || []).join(' ').toLowerCase();
        const description = this.getProjectDescription(p).toLowerCase();
        return title.includes(query) || mainCategory.includes(query) || categories.includes(query) || description.includes(query);
      });
    }
    
    // Apply sorting
    if (this.sortColumn && this.sortDirection) {
      filtered.sort((a, b) => {
        const aValue = this.getProjectTitle(a).toLowerCase();
        const bValue = this.getProjectTitle(b).toLowerCase();
        const comparison = aValue.localeCompare(bValue);
        return this.sortDirection === 'asc' ? comparison : -comparison;
      });
    }
    
    this.filteredProjects = filtered;
  }

  onFilterChange() {
    this.applyFiltersAndSort();
  }

  onSort(column: SortColumn) {
    if (this.sortColumn === column) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortColumn = null;
        this.sortDirection = null;
      } else {
        this.sortDirection = 'asc';
      }
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applyFiltersAndSort();
  }

  getSortIcon(column: SortColumn): string {
    if (this.sortColumn !== column) {
      return 'M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4';
    }
    return this.sortDirection === 'asc' 
      ? 'M5 15l7-7 7 7' 
      : 'M19 9l-7 7-7-7';
  }

  // Project title/description getters
  getProjectTitle(project: Project): string {
    // Show title in first available language, preferring NL
    return project.title_nl || project.title_en || project.title_de || project.title_fr || project.title_es || project.title || 'Untitled';
  }

  getProjectDescription(project: Project): string {
    // Show description in first available language, preferring NL
    const desc = project.description_nl || project.description_en || project.description_de || project.description_fr || project.description_es || project.description || '';
    const plainText = desc.replace(/<[^>]*>/g, '');
    return plainText.substring(0, 100) + (plainText.length > 100 ? '...' : '');
  }

  getProjectImage(project: Project): string {
    if (project.image) return project.image;
    if (project.gallery && project.gallery.length > 0) return project.gallery[0].url;
    return '';
  }

  getFilledLanguages(project: Project): string[] {
    return this.availableLanguages
      .filter(lang => project[`title_${lang.code}`] && project[`title_${lang.code}`].trim())
      .map(lang => lang.code.toUpperCase());
  }

  // Navigation
  navigateToNewProject() {
    this.router.navigate(['/projects/edit/new']);
  }

  navigateToEditProject(project: Project) {
    if (project.id) {
      this.router.navigate(['/projects/edit', project.id]);
    }
  }

  // Delete modal handlers
  openDeleteModal(project: Project) {
    this.projectToDelete = project;
    this.showDeleteModal = true;
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.projectToDelete = null;
  }

  async deleteProject() {
    if (!this.projectToDelete?.id) return;

    try {
      await this.projectService.deleteProject(this.projectToDelete.id);
      this.notificationService.show({
        message: 'Project succesvol verwijderd',
        type: 'success'
      });
      this.closeDeleteModal();
      this.loadProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      this.notificationService.show({
        message: 'Fout bij verwijderen van project',
        type: 'error'
      });
    }
  }

  // Domain management
  toggleDomainEditor(project: Project) {
    if (!project.id) return;
    this.editingDomains[project.id] = !this.editingDomains[project.id];
  }

  isDomainSelected(project: Project, domain: string): boolean {
    return (project.domains || []).includes(domain);
  }

  toggleDomainSelection(project: Project | Partial<Project>, domain: string) {
    if (!project.domains) {
      project.domains = [];
    }
    const index = project.domains.indexOf(domain);
    if (index > -1) {
      project.domains.splice(index, 1);
    } else {
      project.domains.push(domain);
    }
  }

  async saveDomains(project: Project) {
    if (!project.id) return;

    try {
      await this.projectService.updateProjectDomains(project.id, project.domains || []);
      this.notificationService.show({
        message: 'Domeinen succesvol bijgewerkt',
        type: 'success'
      });
      this.editingDomains[project.id] = false;
    } catch (error) {
      console.error('Error updating domains:', error);
      this.notificationService.show({
        message: 'Fout bij bijwerken van domeinen',
        type: 'error'
      });
    }
  }

  getDomainDisplay(project: Project): string {
    const domains = project.domains || [];
    if (domains.length === 0) return 'Geen domeinen';
    return `${domains.length} domein${domains.length === 1 ? '' : 'en'}`;
  }
}
